import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EXPERTISES_DATA } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { useSEO } from "@/lib/useSEO";
import { BookingButton } from "@/components/BookingButton";

export default function Expertises() {
  useSEO({
    title: "Nos domaines d'expertise",
    description:
      "Les quatre piliers d'expertise de HERUS Axiom : gouvernance des projets stratégiques, structuration de projets & PPP, impact & performance, transition énergétique & transformation.",
    keywords: "domaines d'expertise, gouvernance, structuration de projets, PPP, impact, performance, transition énergétique, transformation, HERUS Axiom",
    path: "/expertises",
  });
  return (
    <div data-testid="expertises-page" className="bg-[#0A0A0A]">
      {/* Hero */}
      <section className="pt-40 lg:pt-52 pb-20 lg:pb-28 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal><Overline>Expertises clés</Overline></Reveal>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.95] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-7xl max-w-5xl"
        >
          Quatre piliers
          <br />
          au service de la <span className="italic text-[#C5A059]">décision.</span>
        </motion.h1>
        <Reveal i={2}>
          <p className="mt-10 max-w-2xl text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">
            Nous mobilisons des expertises complémentaires pour accompagner les organisations dans la
            conception, la gouvernance et la transformation de leurs projets, en conjuguant regard
            stratégique, ancrage opérationnel et rigueur de méthode.
          </p>
        </Reveal>
      </section>

      {/* Pillars — alternating, each links to its detail page */}
      {EXPERTISES_DATA.map((e, i) => {
        const light = i % 2 === 1;
        const photoLeft = i % 2 === 1;
        const tCol = light ? "text-[#0A0A0A]" : "text-[#F9F6F0]";
        return (
          <section key={e.slug} className={`border-t border-white/10 ${light ? "bg-[#F9F6F0]" : "bg-[#0A0A0A]"}`}>
            <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              <div className={`lg:col-span-6 ${photoLeft ? "lg:order-1" : "lg:order-2"}`}>
                <Link to={`/expertise/${e.slug}`} data-testid={`pillar-img-${e.slug}`}>
                  <Reveal className="relative overflow-hidden grain group">
                    <img src={e.img} alt={e.alt} className="w-full aspect-[4/3] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
                  </Reveal>
                </Link>
              </div>
              <div className={`lg:col-span-6 ${photoLeft ? "lg:order-2" : "lg:order-1"}`}>
                <Reveal>
                  <span className="font-serif-display text-5xl text-[#C5A059] leading-none block">{e.num}</span>
                  <h2 className={`mt-6 font-serif-display text-4xl lg:text-5xl font-light leading-[0.98] ${tCol}`}>{e.title}</h2>
                  <p className={`mt-5 font-serif-display text-2xl lg:text-3xl font-light italic ${light ? "text-[#0A0A0A]/70" : "text-[#C5A059]"}`}>{e.lead}</p>
                </Reveal>
                <Reveal i={1}>
                  <p className={`mt-8 font-light leading-relaxed ${light ? "text-[#0A0A0A]/65" : "text-[#F9F6F0]/65"}`}>{e.intro}</p>
                </Reveal>
                <Reveal i={2}>
                  <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {e.points.slice(0, 4).map((p) => (
                      <li key={p} className={`flex items-center gap-3 text-sm ${light ? "text-[#0A0A0A]/80" : "text-[#F9F6F0]/80"}`}>
                        <span className="h-1.5 w-1.5 bg-[#C5A059] rotate-45 shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/expertise/${e.slug}`}
                    data-testid={`pillar-cta-${e.slug}`}
                    className={`group inline-flex items-center gap-3 mt-10 text-sm uppercase tracking-[0.2em] border-b pb-2 transition-colors duration-500 ${
                      light ? "text-[#0A0A0A] border-[#C5A059] hover:text-[#C5A059]" : "text-[#F9F6F0]/80 border-white/20 hover:text-[#C5A059] hover:border-[#C5A059]"
                    }`}
                  >
                    Découvrir cette expertise
                    <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      {/* Closing statement + CTA */}
      <section className="relative border-t border-white/10 py-28 lg:py-40 px-6 bg-[#0A0A0A] overflow-hidden">
        <ParticleField opacity={1.3} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/20 via-transparent to-[#0A0A0A]/20" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <Reveal>
            <p className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.2] text-[#F9F6F0]">
              Parce que les projets les plus ambitieux exigent bien plus qu'une expertise technique,
              HERUS associe <span className="text-[#C5A059]">vision stratégique</span>, expérience de
              terrain et <span className="italic">excellence méthodologique</span> pour accompagner des
              transformations durables.
            </p>
          </Reveal>
          <Reveal i={1} className="mt-12 flex flex-wrap items-center justify-center gap-8">
            <Link to="/contact" data-testid="expertises-cta" className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#F9F6F0]/80 hover:text-[#C5A059] border-b border-white/20 hover:border-[#C5A059] pb-2 transition-colors duration-500">
              Échanger sur votre projet
              <ArrowUpRight size={18} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <BookingButton testId="expertises-booking-btn" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
